import { WireAdapter } from "lwc";

import type { apollo } from "@devforce/trailhead-client-apollo";

import * as env from "@devforce/trailhead-client-env";
import { postJSON } from "@devforce/trailhead-client-fetch";
import { getLocale } from "@devforce/trailhead-client-locale";

export type QueryAdapterConfig = {
  options?: QueryAdapterOptions;
  refreshKey?: unknown;
  useFetch?: boolean;
};

export type QueryAdapterOptions = Omit<apollo.WatchQueryOptions, "query"> & {
  query: string;
};

export type QueryAdapterValue<T> = apollo.ApolloQueryResult<T>;

export type QueryAdapterVariables = apollo.OperationVariables;

let apolloPromise:
  | Promise<typeof import("@devforce/trailhead-client-apollo")>
  | undefined = undefined;

// Enable Prettier formatting and IDE syntax highlighting
// The adpater will convert the string to a DocumentNode before running the query
export function gql(
  chunks: TemplateStringsArray,
  ...variables: unknown[]
): string {
  return chunks.reduce(
    (accumulator, chunk, index) =>
      `${accumulator}${chunk}${index in variables ? variables[index] : ""}`,
    ""
  );
}

export class QueryAdapter<T> implements WireAdapter {
  private query: apollo.ObservableQuery<T, Record<string, unknown>> | null =
    null;

  private querySubscription: apollo.ObservableSubscription | null = null;

  private result: QueryAdapterValue<T> | null = null;

  private setValue: (value: QueryAdapterValue<T>) => void;

  constructor(setValue: (value: QueryAdapterValue<T>) => void) {
    this.setValue = setValue;
  }

  connect() {}

  disconnect() {
    this.querySubscription?.unsubscribe();
  }

  async update({ options, useFetch }: QueryAdapterConfig) {
    if (!options) return;
    if (useFetch) {
      return this.fetchQuery(options);
    }
    if (!apolloPromise) {
      apolloPromise = import("@devforce/trailhead-client-apollo");
    }
    let { apollo, getClient } = await apolloPromise;
    let { query, ...watchOptions } = options;
    this.query = getClient().watchQuery<T>({
      // Queries are passed in as strings so components don't need to import Apollo
      query: apollo.gql`${query}`,
      ...watchOptions,
    });
    this.querySubscription?.unsubscribe();
    this.querySubscription = this.query.subscribe(
      (result) => {
        this.result = result;
        this.setValue(result);
      },
      (error: apollo.ApolloError) => {
        this.result = {
          ...this.result,
          error,
          data: null!,
          loading: false,
          networkStatus: apollo.NetworkStatus.error,
        };
        this.setValue(this.result);
      }
    );
  }

  private async fetchQuery(options: QueryAdapterOptions) {
    try {
      let context = (options.context ?? {}) as {
        uri?: string;
        headers?: Record<string, string>;
      };
      let uri: string = context?.uri ?? env.get("EXPERIENCE_API_HOST");
      let headers: Record<string, string> = Object.assign(
        {
          "Accept-Language": getLocale(),
        },
        context?.headers ?? {}
      );
      let body = JSON.stringify({
        query: options.query,
        variables: options.variables,
      });
      let { data, error, errors } = await postJSON<
        Pick<QueryAdapterValue<T>, "data" | "errors" | "error">
      >(uri, body, { headers });
      if (error || errors) {
        this.setValue({
          error,
          errors,
          data: null!,
          loading: false,
          networkStatus: -1,
        });
      }
      if (data) {
        this.setValue({
          data,
          loading: false,
          networkStatus: -1,
        });
      }
    } catch (e) {
      this.setValue({
        error: e as apollo.ApolloError,
        data: null!,
        loading: false,
        networkStatus: -1,
      });
    }
  }
}
